import type { ReactNode, InputHTMLAttributes } from 'react';

/**
 * Minimal, friendly UI primitives. Rounder corners, more whitespace,
 * warmer palette — the goal is calm, not clever.
 */

export function Page({ title, subtitle, actions, children }: {
  title?: string; subtitle?: string; actions?: ReactNode; children: ReactNode;
}) {
  return (
    <main className="mx-auto max-w-5xl px-4 py-8">
      {(title || actions) && (
        <header className="mb-6 flex items-start justify-between gap-4">
          <div>
            {title && <h1 className="text-2xl font-semibold tracking-tight">{title}</h1>}
            {subtitle && <p className="mt-1 text-sm text-slate-500">{subtitle}</p>}
          </div>
          <div className="flex items-center gap-2">{actions}</div>
        </header>
      )}
      {children}
    </main>
  );
}

export function Card({ children, className = '', tone }: {
  children: ReactNode; className?: string; tone?: 'good' | 'warn' | 'bad' | 'accent';
}) {
  const toneClass = tone === 'good'   ? 'ring-1 ring-emerald-200 dark:ring-emerald-900/50'
                  : tone === 'warn'   ? 'ring-1 ring-amber-200  dark:ring-amber-900/50'
                  : tone === 'bad'    ? 'ring-1 ring-red-200    dark:ring-red-900/50'
                  : tone === 'accent' ? 'ring-1 ring-sky-200    dark:ring-sky-900/50'
                  : '';
  return (
    <section className={`rounded-xl border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-700 dark:bg-slate-800 ${toneClass} ${className}`}>
      {children}
    </section>
  );
}

export function Button({
  children, variant = 'primary', size = 'md', ...rest
}: {
  children: ReactNode;
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger';
  size?: 'md' | 'sm';
} & React.ButtonHTMLAttributes<HTMLButtonElement>) {
  const sizes = size === 'sm' ? 'px-3 py-1.5 text-sm' : 'px-4 py-2 text-sm';
  const styles = {
    primary:   'bg-slate-900 text-white hover:bg-slate-800 dark:bg-slate-100 dark:text-slate-900 dark:hover:bg-white',
    secondary: 'border border-slate-300 bg-white hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-800 dark:hover:bg-slate-700',
    ghost:     'text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-700',
    danger:    'border border-red-500 text-red-700 hover:bg-red-50 dark:border-red-500 dark:text-red-400 dark:hover:bg-red-950',
  }[variant];
  return (
    <button
      {...rest}
      className={`rounded-lg font-medium transition-colors disabled:opacity-60 ${sizes} ${styles} ${rest.className ?? ''}`}
    >
      {children}
    </button>
  );
}

export function Field({ label, hint, error, children }: {
  label: string; hint?: string; error?: string; children: ReactNode;
}) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-200">{label}</span>
      {children}
      {hint && !error && <span className="mt-1 block text-xs text-slate-500">{hint}</span>}
      {error && <span className="mt-1 block text-xs text-red-600">{error}</span>}
    </label>
  );
}

export function TextInput(props: InputHTMLAttributes<HTMLInputElement>) {
  return (
    <input
      {...props}
      className={`w-full rounded-lg border border-slate-300 bg-white px-3 py-2 outline-none focus:border-slate-500 focus:ring-2 focus:ring-slate-200 dark:border-slate-600 dark:bg-slate-800 dark:focus:ring-slate-700 ${props.className ?? ''}`}
    />
  );
}

export function Table({ children }: { children: ReactNode }) {
  return (
    <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-800">
      <table className="w-full text-sm">{children}</table>
    </div>
  );
}

export function Empty({ children }: { children: ReactNode }) {
  return (
    <p className="rounded-xl border border-dashed border-slate-300 bg-slate-50 p-8 text-center text-sm text-slate-500 dark:border-slate-600 dark:bg-slate-900/30">
      {children}
    </p>
  );
}

export function Badge({ children, tone = 'neutral' }: { children: ReactNode; tone?: 'neutral' | 'good' | 'warn' | 'bad' }) {
  const styles = {
    neutral: 'bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-100',
    good:    'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200',
    warn:    'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200',
    bad:     'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-200',
  }[tone];
  return <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium ${styles}`}>{children}</span>;
}
